import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import {
  SERVICE_STAFF_ROLES,
  SERVICE_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

/**
 * Assignment Fields
 * User assignment and creation tracking fields.
 *
 * `savedViewMembership` mirrors `../saved-views/ticket-saved-views.ts`.
 */
export const assignmentFields = defineFieldConfigMap({
  // FIELD: assigned_to
  assigned_to: {
    type: "user",
    label: "Assigned To",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "fk_tickets_assigned_to",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Assign ticket to user...",
    required: false,
    formSpan: 3,
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    // Quick-filter as a user picker (Me + multi-select). Values are app_users.id[];
    // converted to a GraphQL `in` on the scalar assigned_to column.
    filterType: "user-select",
    width: 180,
    minWidth: 150,
    maxWidth: 250,
    icon: "User",
    groupable: true,
    // Role-based access control
    allowedRoles: [...SERVICE_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 3,
    mobileDetailSectionId: "assignment",
    graphqlSelector: "assigned_to",
    savedViewMembership: [
      "all-tickets",
      "high-priority",
      "open-tickets",
      "overdue-tickets",
      "no-time-entries",
      "with-time-entries",
    ],
    /**
     * "needs-first-response" filter token: open tickets nobody has picked up yet
     * (no assignee + not resolved/closed/cancelled). Bundles both conditions in
     * one field's output — mirrors the `due_date: "overdue"` token pattern — so
     * it doesn't depend on cross-field filter merging. Any other value falls
     * through to the default exact conversion.
     */
    toGraphQLFilter: (value) => {
      if (value !== "needs-first-response") return null;
      return {
        assigned_to: { is: "NULL" },
        status: { in: ["new", "assigned", "in_progress"] },
      };
    },
  },

  // FIELD: created_by
  created_by: {
    type: "text",
    label: "Created By",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "fk_tickets_created_by",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Ticket creator",
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    // Same user picker as `assigned_to` — "who opened it" is a first-class filter.
    filterType: "user-select",
    width: 180,
    minWidth: 150,
    maxWidth: 250,
    icon: "User",
    groupable: true,
    // Role-based access control
    allowedRoles: [...SERVICE_STAFF_ROLES],
    mobileDetailSectionId: "assignment",
    graphqlSelector: "created_by",
  },
});
